import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, BarChart3, ExternalLink, Copy, Trash2, QrCode, IdCard } from "lucide-react";
import { mediaUrl, publicUrl } from "@/lib/cartes";

export default function MesCartes() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cartes/mine");
      setItems(data.items || []);
    } catch { toast.error("Impossible de charger vos cartes."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Reprise d'un brouillon créé depuis la page commerciale (avant inscription)
  useEffect(() => {
    const raw = localStorage.getItem("macarte_draft");
    if (!raw) return;
    localStorage.removeItem("macarte_draft");
    (async () => {
      try {
        const d = JSON.parse(raw);
        if (!d.card_type || !d.template_id) return;
        const { data } = await api.post("/cartes", { card_type: d.card_type, template_id: d.template_id });
        const body = {};
        ["identity", "contact", "about", "actions"].forEach((k) => { if (d[k]) body[k] = d[k]; });
        if (Object.keys(body).length) await api.put(`/cartes/${data.id}`, body);
        toast.success("Votre carte a bien été créée ! Finalisez-la puis publiez-la.");
        nav(`/ma-carte/${data.id}/editer`);
      } catch { /* brouillon ignoré en cas d'erreur */ }
    })();
  }, [nav]);

  const duplicate = async (id) => {
    try { const { data } = await api.post(`/cartes/${id}/duplicate`); toast.success("Carte dupliquée."); nav(`/ma-carte/${data.id}/editer`); }
    catch { toast.error("Échec de la duplication."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette carte ?")) return;
    try { await api.delete(`/cartes/${id}`); toast.success("Carte supprimée."); setItems((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Échec de la suppression."); }
  };

  return (
    <div data-testid="mes-cartes-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2"><IdCard className="text-[#E05638]" /> Ma Carte Proxi</h1>
          <p className="text-slate-500 text-sm mt-1">Votre identité. Votre réseau. Votre carte.</p>
        </div>
        <Button onClick={() => nav("/ma-carte/nouvelle")} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="create-card-btn">
          <Plus size={18} className="mr-1" /> Créer une carte
        </Button>
      </div>

      {loading ? <div className="text-slate-400 py-16 text-center">Chargement…</div> :
        items.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="cartes-empty">
            <IdCard className="mx-auto text-slate-300 mb-3" size={48} />
            <p className="text-slate-500 mb-4">Vous n'avez pas encore de carte digitale.</p>
            <Button onClick={() => nav("/ma-carte/nouvelle")} className="bg-[#E05638] hover:bg-[#c94a2f]"><Plus size={18} className="mr-1" /> Créer ma première carte</Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cartes-list">
            {items.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid={`carte-item-${c.id}`}>
                <div className="h-24 flex items-center gap-3 px-4" style={{ background: c.colors?.primary || "#0f172a" }}>
                  {(c.logo_url || c.photo_url) ? (
                    <img src={mediaUrl(c.logo_url || c.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white/70" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">{(c.title || "?").charAt(0)}</div>
                  )}
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{c.title}</div>
                    <Badge className={`mt-1 ${c.status === "published" ? "bg-emerald-500" : "bg-slate-400"}`}>{c.status === "published" ? "Publiée" : "Brouillon"}</Badge>
                  </div>
                </div>
                <div className="p-3 flex flex-wrap gap-1.5 mt-auto">
                  <Button size="sm" variant="outline" onClick={() => nav(`/ma-carte/${c.id}/editer`)} data-testid={`edit-${c.id}`}><Pencil size={14} className="mr-1" /> Éditer</Button>
                  <Button size="sm" variant="outline" onClick={() => nav(`/ma-carte/${c.id}/stats`)} data-testid={`stats-${c.id}`}><BarChart3 size={14} /></Button>
                  {c.status === "published" && <a href={publicUrl(c.slug)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`view-${c.id}`}><ExternalLink size={14} /></Button></a>}
                  <Button size="sm" variant="outline" onClick={() => duplicate(c.id)} data-testid={`dup-${c.id}`}><Copy size={14} /></Button>
                  <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => remove(c.id)} data-testid={`del-${c.id}`}><Trash2 size={14} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
